/**
 * Automation-screen formatting and scoring-derivation helpers, kept pure so they
 * are unit-testable away from React.
 *
 * The backend stores the opportunity `band` string ("80-100" … "0-39", spec
 * §8.1) alongside the integer score; the visual band (colour + human label) is
 * derived here so the UI stays consistent with the server's banding. The three
 * sub-scores shown in the "why this scored X" breakdown are re-derived from the
 * real signals with the *same* maths the backend uses
 * (`app/services/automation_scoring.py`): volume is log-scaled against the
 * busiest cluster in the ranking, feasibility is the mean of average confidence
 * and format consistency, repetitiveness is `1 - other_rate`. Every number the
 * UI shows therefore traces back to an API field — nothing is invented.
 */
import type { OpportunityCluster, OpportunitySignals, OpportunitySpans } from '../../api/types';

/** Big-number formatting: `1,234` · `41k` · `1.8M` (design-reference §4.2). */
export function fmtBig(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${String(Math.round(n / 1000))}k`;
  return Math.round(n).toLocaleString('en-GB');
}

/** A `[0,1]` fraction as a percentage string, `7.9%` (one decimal by default). */
export function fmtPct(fraction: number, decimals = 1): string {
  return `${(fraction * 100).toFixed(decimals)}%`;
}

/** A `[0,1]` confidence as a two-decimal figure, `0.98`. */
export function fmtConfidence(value: number): string {
  return value.toFixed(2);
}

export interface BandStyle {
  /** Background token for the score badge / action pill. */
  bg: string;
  /** Foreground (text) token. */
  fg: string;
  /** Human band label, e.g. `Strong`. */
  label: string;
}

/**
 * Map an opportunity's stored band string (falling back to its score) to the
 * badge colour tokens and a human label. The four §8.1 bands
 * (`80-100`/`60-79`/`40-59`/`0-39`) map to Strong/Promising/Emerging/Low. Colour
 * follows the design-reference §4.8 mapping (Strong→green, Promising→accent,
 * Emerging→warn); the lowest band uses the neutral scheme so "not a priority"
 * reads as muted rather than alarming.
 */
export function bandStyle(band: string, score: number): BandStyle {
  const tier = bandTier(band, score);
  switch (tier) {
    case 'strong':
      return { bg: 'var(--ok-bg)', fg: 'var(--ok-ink)', label: 'Strong' };
    case 'promising':
      return { bg: 'var(--a-soft)', fg: 'var(--a-ink)', label: 'Promising' };
    case 'emerging':
      return { bg: 'var(--warn-bg)', fg: 'var(--warn)', label: 'Emerging' };
    default:
      return { bg: 'var(--hov)', fg: 'var(--t2)', label: 'Low' };
  }
}

type BandTier = 'strong' | 'promising' | 'emerging' | 'low';

/** Resolve the band tier, preferring the server band string, else the score. */
function bandTier(band: string, score: number): BandTier {
  switch (band) {
    case '80-100':
      return 'strong';
    case '60-79':
      return 'promising';
    case '40-59':
      return 'emerging';
    case '0-39':
      return 'low';
    default:
      if (score >= 80) return 'strong';
      if (score >= 60) return 'promising';
      if (score >= 40) return 'emerging';
      return 'low';
  }
}

/** Normalised volume sub-score: log-scaled document count against `reference`. */
export function volumeNorm(volume: number, reference: number): number {
  if (volume <= 0) return 0;
  if (reference <= 0) return 1;
  return clamp01(Math.log1p(volume) / Math.log1p(reference));
}

/** Normalised feasibility sub-score: mean of avg confidence and format consistency. */
export function feasibilityNorm(avgConfidence: number, formatConsistency: number): number {
  return clamp01((clamp01(avgConfidence) + clamp01(formatConsistency)) / 2);
}

/** Normalised repetitiveness sub-score: `1 - other_rate`. */
export function repetitivenessNorm(otherRate: number): number {
  return clamp01(1 - clamp01(otherRate));
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

export interface SubScore {
  key: 'volume' | 'feasibility' | 'repetitiveness';
  label: string;
  /** Normalised `[0,1]` value. */
  value: number;
  /** Weight note, e.g. `50% weight`. */
  weight: string;
}

/**
 * The three weighted sub-scores behind an opportunity's score, in the order the
 * drawer renders them. `reference` is the busiest cluster's volume in the current
 * ranking (the same reference the backend normalises against).
 */
export function subScores(
  signals: OpportunitySignals,
  reference: number,
): [SubScore, SubScore, SubScore] {
  return [
    {
      key: 'volume',
      label: 'Volume',
      value: volumeNorm(signals.volume, reference),
      weight: '50% weight',
    },
    {
      key: 'feasibility',
      label: 'Feasibility',
      value: feasibilityNorm(signals.avg_confidence, signals.format_consistency),
      weight: '30% weight',
    },
    {
      key: 'repetitiveness',
      label: 'Repetitiveness',
      value: repetitivenessNorm(signals.other_rate),
      weight: '20% weight',
    },
  ];
}

export interface TrendView {
  /** Signed, human trend, `+12%` · `0%` · `−2%`. */
  text: string;
  /** Colour tone: rising is good, falling is bad, flat is neutral. */
  tone: 'ok' | 'bad' | 'neutral';
}

/**
 * Present the real `trend` signal (a monthly volume slope) as a signed
 * percentage with a colour tone. V1 reports a flat trend; this renders it
 * honestly as `0%` rather than inventing a movement.
 */
export function trendView(trend: number): TrendView {
  const pct = Math.round(trend * 100);
  if (pct > 0) return { text: `+${String(pct)}%`, tone: 'ok' };
  if (pct < 0) return { text: `−${String(Math.abs(pct))}%`, tone: 'bad' };
  return { text: '0%', tone: 'neutral' };
}

/** The trend colour token for a tone. */
export function trendColor(tone: TrendView['tone']): string {
  if (tone === 'ok') return 'var(--ok-ink)';
  if (tone === 'bad') return 'var(--bad)';
  return 'var(--t3)';
}

/** The document-type title of a cluster (its classification category name). */
export function clusterTitle(cluster: OpportunityCluster): string {
  return cluster.category_name;
}

/** English pluralisation for the small counts this line reports. */
function plural(count: number, noun: string): string {
  return `${String(count)} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * The card's context line.
 *
 * A card is one *document type*, merged across the location/scanner/source
 * dimensions the clusters are stored by, so this reports the breadth it covers
 * ("3 locations · 3 scanners · api") rather than naming one arbitrary
 * location — which is what made the same type appear three times over.
 *
 * When a location filter is active the server merges within that location, so
 * `spans.locations` is 1 and `cluster.location_id` names it: the line then
 * resolves the real name, which is the more useful thing to show.
 */
export function clusterContext(
  cluster: OpportunityCluster,
  locationName: (id: string) => string | undefined,
  scannerName: (id: string) => string | undefined,
  spans?: OpportunitySpans,
): string {
  const parts: string[] = [];

  if (cluster.location_id != null) {
    parts.push(locationName(cluster.location_id) ?? 'Unnamed location');
  } else if (spans != null && spans.locations > 0) {
    parts.push(plural(spans.locations, 'location'));
  }

  if (cluster.scanner_id != null) {
    parts.push(scannerName(cluster.scanner_id) ?? 'Unnamed scanner');
  } else if (spans != null && spans.scanners > 0) {
    parts.push(plural(spans.scanners, 'scanner'));
  }

  // `source` is "multiple" when the merge spanned channels; the spans list has
  // the actual names, which is more informative than that placeholder.
  if (spans != null && spans.sources.length > 1) {
    parts.push(spans.sources.join(' + '));
  } else {
    parts.push(cluster.source);
  }
  return parts.join(' · ');
}

/**
 * A stable 1–6 seed for an opportunity's trend polyline, hashed from its id so
 * each opportunity keeps a distinct-but-stable shape across renders (§5.7).
 */
export function seedFor(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) % 100_000;
  }
  return (hash % 6) + 1;
}
